import { z } from "zod";
/** Native networking is additive to the installed app's box and settings bridge. */
export const HEYDEX_NETWORK_VERSION = 1;
export const NETWORK_REQUEST_BYTES = 2 * 1024 * 1024;
export const NETWORK_RESPONSE_BYTES = 16 * 1024 * 1024;
export const NETWORK_TIMEOUT_MS = 120000;
const page = z.uuid();
const id = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const bounded = z.number().int().positive();
const body = (max: number) =>
  z
    .string()
    .max(Math.ceil(max / 3) * 4)
    .regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/)
    .refine(
      (value) =>
        (value.length / 4) * 3 - (value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0) <= max,
    );
const headers = z
  .record(
    z
      .string()
      .min(1)
      .max(128)
      .regex(/^[!#$%&'*+.^_`|~\w-]+$/),
    z
      .string()
      .max(8192)
      .refine((value) => !/[\r\n\0]/.test(value)),
  )
  .refine(
    (values) =>
      Object.entries(values).reduce(
        (total, [name, value]) => total + name.length + value.length,
        0,
      ) <= 32768,
  );
export const NetworkRequest = z
  .object({
    url: z
      .url()
      .max(8192)
      .refine((value) => {
        const url = new URL(value);
        return (
          ["http:", "https:"].includes(url.protocol) && !url.username && !url.password && !url.port
        );
      }),
    method: z.enum(["GET", "HEAD", "POST", "PUT", "PATCH", "DELETE", "OPTIONS"]),
    headers: headers.refine((values) =>
      Object.keys(values).every(
        (name) =>
          ![
            "authorization",
            "proxy-authorization",
            "cookie",
            "cookie2",
            "host",
            "connection",
            "content-length",
            "transfer-encoding",
            "accept-encoding",
          ].includes(name.toLowerCase()),
      ),
    ),
    body: body(NETWORK_REQUEST_BYTES).optional(),
    timeoutMs: bounded.max(NETWORK_TIMEOUT_MS),
    firstByteMs: bounded.max(NETWORK_TIMEOUT_MS),
    maxBytes: bounded.max(NETWORK_RESPONSE_BYTES),
  })
  .strict()
  .refine((value) => !(["GET", "HEAD"].includes(value.method) && value.body !== undefined));
const address = z
  .object({ address: z.string().min(1).max(253), family: z.union([z.literal(4), z.literal(6)]) })
  .strict();
export const NetworkAddresses = z.array(address).max(64);
export const NetworkResponse = z
  .object({
    status: z.number().int().min(100).max(599),
    headers,
    body: body(NETWORK_RESPONSE_BYTES),
  })
  .strict();
const Ready = z.object({ type: z.literal("networkReady"), page }).strict();
const Call = z.discriminatedUnion("operation", [
  z
    .object({
      type: z.literal("network"),
      page,
      id,
      operation: z.literal("resolve"),
      host: z
        .string()
        .min(1)
        .max(253)
        .regex(
          /^(?:[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?)(?:\.[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?)*\.?$/i,
        ),
    })
    .strict(),
  z
    .object({
      type: z.literal("network"),
      page,
      id,
      operation: z.literal("request"),
      request: NetworkRequest,
    })
    .strict(),
]);
const ReadyReply = z
  .object({ type: z.literal("networkReady"), page, version: z.literal(HEYDEX_NETWORK_VERSION) })
  .strict();
const Reply = z.union([
  z
    .object({
      type: z.literal("networkReply"),
      page,
      id,
      operation: z.literal("resolve"),
      ok: z.literal(true),
      addresses: NetworkAddresses,
    })
    .strict(),
  z
    .object({
      type: z.literal("networkReply"),
      page,
      id,
      operation: z.literal("request"),
      ok: z.literal(true),
      response: NetworkResponse,
    })
    .strict(),
  z
    .object({
      type: z.literal("networkReply"),
      page,
      id,
      ok: z.literal(false),
      message: z.string().min(1).max(1000),
    })
    .strict(),
]);
export type HeyDexNetworkRequest = z.infer<typeof NetworkRequest>;
export type HeyDexNetworkResponse = z.infer<typeof NetworkResponse>;
export type HeyDexNetworkAddress = z.infer<typeof address>;
export type HeyDexNetworkCall = z.infer<typeof Call>;
const Close = z.object({ type: z.literal("networkClose"), page }).strict();
export type HeyDexNetworkEvent = z.infer<typeof Ready> | z.infer<typeof Close> | HeyDexNetworkCall;
export type HeyDexNetworkCommand = z.infer<typeof ReadyReply> | z.infer<typeof Reply>;
export function parseNetworkEvent(value: unknown): HeyDexNetworkEvent | null {
  const result = z.union([Ready, Close, Call]).safeParse(value);
  return result.success ? result.data : null;
}
export function parseNetworkCommand(value: unknown): HeyDexNetworkCommand | null {
  const result = z.union([ReadyReply, Reply]).safeParse(value);
  return result.success ? result.data : null;
}
